import React, { useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Badge from "@mui/material/Badge";
import CircularProgress from "@mui/material/CircularProgress";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { bindMenu, bindTrigger, usePopupState } from "material-ui-popup-state/hooks";
import WarehouseLocationsInfoDialog from "./WarehouseLocationsInfoDialog";
import useMediaQuery from "@mui/material/useMediaQuery";
import WarehouseFilter from "../WarehouseFilter";
import Toolkit from "../Toolkit";
import SearchScanner from "../../../components/capture/SearchScanner";
import WarehouseName from "./WarehouseName";
import WidgetCard from "./WidgetCard";
import AddLocationButton from "./addLocation/AddLocationButton";
import FiltersStatus from "./FiltersStatus";

function WarehouseBulkActions({ bulkActions }) {
  const popupState = usePopupState({
    variant: "popover",
    popupId: "warehouse-location-bulk-actions",
  });

  if (!bulkActions) return null;

  const handleAction = action => {
    popupState.close();
    action();
  };

  return (
    <>
      <Badge badgeContent={bulkActions.selectedCount} color="primary" max={999999}>
        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={bulkActions.loading}
          endIcon={
            bulkActions.loading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <KeyboardArrowDownIcon />
            )
          }
          {...bindTrigger(popupState)}
        >
          Bulk Actions
        </Button>
      </Badge>
      <Menu {...bindMenu(popupState)}>
        <MenuItem
          disabled={bulkActions.loading || !bulkActions.canActivateSelected}
          onClick={() => handleAction(bulkActions.onActivate)}
        >
          <ListItemText
            primary={`Activate Selected Locations (${bulkActions.selectedCount})`}
          />
        </MenuItem>
        <MenuItem
          disabled={bulkActions.loading || !bulkActions.canDeactivateSelected}
          onClick={() => handleAction(bulkActions.onDeactivate)}
        >
          <ListItemText
            primary={`Deactivate Selected Locations (${bulkActions.selectedCount})`}
          />
        </MenuItem>
        {bulkActions.onExportExcel && (
          <MenuItem
            disabled={bulkActions.loading}
            onClick={() => handleAction(bulkActions.onExportExcel)}
          >
            <ListItemText
              primary={`Export to Excel (${bulkActions.selectedCount})`}
            />
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

export default function WarehouseHeader({
  view,
  warehouse,
  handleRefetch,
  selectedProjects,
  setSelectedProjects,
  search,
  changeSearch,
  modal,
  changeWarehouse,
  changeView,
  occupied,
  active,
  setOccupied,
  setActive,
  height,
  setHeight,
  locationTypes,
  setLocationTypes,
  clearFilters,
  includesMobileLocations = false,
  occupiedCount = 0,
  totalCount = 0,
  warehouseOccupiedCount = 0,
  warehouseTotalCount = 0,
  bulkActions,
}) {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("sm"));
  const isDesktop = useMediaQuery(theme => theme.breakpoints.up("md"));

  const hasActiveSearch =
    search ||
    occupied !== null ||
    active !== true ||
    height ||
    locationTypes?.length > 0 ||
    selectedProjects?.length > 0;
  const locationLabel = includesMobileLocations ? "Locations" : "Base Locations";
  const emptyLocationLabel = includesMobileLocations ? "Empty Locations" : "Empty Base Locations";
  const occupiedLocationLabel = includesMobileLocations
    ? "Occupied Locations"
    : "Occupied Base Locations";
  const emptyWidgetLabel = isMobile
    ? includesMobileLocations
      ? "Empty"
      : "Empty Base"
    : emptyLocationLabel;
  const occupiedWidgetLabel = isMobile
    ? includesMobileLocations
      ? "Occupied"
      : "Occupied Base"
    : occupiedLocationLabel;
  const widgetOccupied = hasActiveSearch ? occupiedCount : warehouseOccupiedCount;
  const widgetTotal = hasActiveSearch ? totalCount : warehouseTotalCount;

  const emptySlots = widgetTotal - widgetOccupied;
  const displayEmptySlots = emptySlots < 0 ? "N/A" : emptySlots;

  const displayOccupancy =
    emptySlots < 0
      ? "N/A"
      : widgetTotal === 0
      ? "0%"
      : `${Math.round((100 / widgetTotal) * widgetOccupied)}%`;

  const lg = modal ? 4 : 3;

  const [emptyInfoOpen, setEmptyInfoOpen] = useState(false);
  const [occupiedInfoOpen, setOccupiedInfoOpen] = useState(false);

  const warehouseId = warehouse?.id;

  return (
    <>
      <Grid container spacing={{ xs: 1, sm: 2, md: 3 }}>
        <Grid size={{ xs: 4, lg: lg }}>
          <WarehouseName warehouse={warehouse} changeWarehouse={changeWarehouse} />
        </Grid>
        <Grid size={{ xs: 4, lg: lg }}>
          <WidgetCard
            value={displayEmptySlots}
            label={emptyWidgetLabel}
            icon={
              <Box display="inline-block" bgcolor="#fff8db" p={3} borderRadius="50%">
                <InsightsOutlinedIcon htmlColor="#efbe25" />
              </Box>
            }
            onInfo={() => setEmptyInfoOpen(true)}
          />
        </Grid>
        <Grid size={{ xs: 4, lg: lg }}>
          <WidgetCard
            value={displayOccupancy}
            label={occupiedWidgetLabel}
            icon={
              <Box display="inline-block" bgcolor="#f0eeec" p={3} borderRadius="50%">
                <InsertChartOutlinedIcon htmlColor="#6b625c" />
              </Box>
            }
            onInfo={() => setOccupiedInfoOpen(true)}
          />
        </Grid>
      </Grid>
      <Box mb={2} />
      {!isDesktop || modal ? (
        <>
          <WarehouseFilter
            occupied={occupied}
            active={active}
            selected={selectedProjects}
            setSelected={setSelectedProjects}
            setOccupied={setOccupied}
            setActive={setActive}
            height={height}
            setHeight={setHeight}
            warehouseId={warehouse.id}
            locationTypes={locationTypes}
            setLocationTypes={setLocationTypes}
            clearFilters={clearFilters}
          />
          {bulkActions && (
            <Box mt={1}>
              <WarehouseBulkActions bulkActions={bulkActions} />
            </Box>
          )}

          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box mt={1} lineHeight={1}>
              {totalCount} {locationLabel}
            </Box>
            <Toolkit refetch={handleRefetch} view={view} changeView={changeView} />
          </Box>
          <Box display="flex" gap={1} mt={1}>
            <Box flexGrow={1}>
              <SearchScanner placeholder="Search" value={search} setSearch={changeSearch} />
            </Box>
            <AddLocationButton warehouse={warehouse} onCreated={handleRefetch} />
          </Box>
        </>
      ) : (
        <>
          <Box pb={2} lineHeight={1} display="flex" justifyContent="space-between">
            <Box>
              {totalCount} {locationLabel}
            </Box>
            <FiltersStatus
              warehouse={warehouse}
              occupied={occupied}
              selected={selectedProjects}
              height={height}
              locationTypes={locationTypes}
              active={active}
            />
          </Box>
          <Box display="flex">
            <Box flexGrow={1}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" justifyContent="flex-start" width="35%" gap={1}>
                  <Box flexGrow={1}>
                    <SearchScanner placeholder="Search" value={search} setSearch={changeSearch} />
                  </Box>
                  <AddLocationButton warehouse={warehouse} onCreated={handleRefetch} />
                </Box>
                <Box display="flex" justifyContent="center" width="25%">
                  <WarehouseFilter
                    occupied={occupied}
                    active={active}
                    selected={selectedProjects}
                    setSelected={setSelectedProjects}
                    setOccupied={setOccupied}
                    setActive={setActive}
                    height={height}
                    setHeight={setHeight}
                    warehouseId={warehouse.id}
                    locationTypes={locationTypes}
                    setLocationTypes={setLocationTypes}
                    clearFilters={clearFilters}
                  />
                  {bulkActions && (
                    <Box ml={1}>
                      <WarehouseBulkActions bulkActions={bulkActions} />
                    </Box>
                  )}
                </Box>
                <Box display="flex" justifyContent="flex-end" width="30%">
                  <Toolkit refetch={handleRefetch} view={view} changeView={changeView} />
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      )}

      <WarehouseLocationsInfoDialog
        open={emptyInfoOpen}
        title={emptyLocationLabel}
        warehouseId={warehouseId}
        variant="empty"
        onClose={() => setEmptyInfoOpen(false)}
      />

      <WarehouseLocationsInfoDialog
        open={occupiedInfoOpen}
        title={occupiedLocationLabel}
        warehouseId={warehouseId}
        variant="occupied"
        onClose={() => setOccupiedInfoOpen(false)}
      />
    </>
  );
}
